import axios from "axios";

import { updateTrustPilotReviewCount, updateTrustPilotScore } from "@App/appReducer";
import config from "@Config/index";
import {
  getTrustpilotData,
  nationalityForHomepage,
} from "@Pages/Search/components/SearchList/components/TrustPilotBanner/helper";
import { AppThunk } from "@Store/index";
import { logger } from "helpers/logger";

import pickReviews from "../components/Reviews/pickReviews";
import {
  fetchStaysNear,
  fetchTopUniversities,
  toStayRows,
} from "../components/UniversityStays/api";
import { countryFromRequest, DEFAULT_MARKET, marketFromCountry } from "../market";
import {
  setHomeV3AppBanner,
  setHomeV3Country,
  setHomeV3Market,
  setHomeV3Reviews,
  setHomeV3Stays,
  setHomeV3Universities,
} from "./homeV3Reducer";

/**
 * Server only: the v3 sections that otherwise appear only after hydration — the review
 * cards, the Trustpilot count and score, and "Stays near top universities" (its pills
 * and the first university's stays) — fetched during SSR so they are in the HTML.
 *
 * All of it shares one SSR_BUDGET_MS deadline and fails soft: a slow or failed source leaves
 * its slice empty, and that section fetches in the browser exactly as before.
 */

// One deadline for the whole fetch, measured from its start, so the page's response
// waits at most this long on top of the live homepage's own data.
const SSR_BUDGET_MS = 1500;

const deadline =
  (endsAt: number) =>
  <T>(promise: Promise<T>, onTimeout?: () => void): Promise<T | null> =>
    new Promise((resolve) => {
      const timer = setTimeout(() => {
        onTimeout?.();
        resolve(null);
      }, Math.max(0, endsAt - Date.now()));
      promise
        .then((value) => resolve(value))
        .catch(() => resolve(null))
        .finally(() => clearTimeout(timer));
    });

const fetchHomeV3Data =
  (props: any): AppThunk =>
  async (dispatch, getState: any) => {
    if (__CLIENT__) return null;
    // China is always GB. Elsewhere: CloudFront's country header, else the userCountry
    // cookie. Unknown → null, and the browser looks it up. The country itself is kept too,
    // for the sign-in's default dialling code.
    const country = config.IS_CHINA_APP ? null : countryFromRequest(props?.req);
    let resolved = country ? marketFromCountry(country) : null;
    if (config.IS_CHINA_APP) resolved = DEFAULT_MARKET;
    dispatch(setHomeV3Market(resolved));
    dispatch(setHomeV3Country(country));
    // the phone's top app banner, unless dismissed (the live banner's own cookie)
    dispatch(setHomeV3AppBanner(props?.req?.cookies?.showAppBanner !== "false"));
    const market = resolved || DEFAULT_MARKET;
    const lang = getState()?.appData?.userLanguage || "en";
    const currency = getState()?.appData?.userCurrency || "";
    const source = axios.CancelToken.source();
    const within = deadline(Date.now() + SSR_BUDGET_MS);

    const reviewsNationality = nationalityForHomepage(country);
    const reviewsTask = within(
      getTrustpilotData(null, reviewsNationality, null, source.token),
      () => source.cancel("ssr budget"),
    ).then((data: any) => {
      if (Array.isArray(data)) dispatch(setHomeV3Reviews(pickReviews(data)));
    });

    const trustpilotTask = within(
      axios.get("/api/v0/trustpilot_review/get_trustpilot_reviews_count"),
    ).then((res: any) => {
      const count = parseInt(res?.data?.data?.count || "0", 10);
      const score = Number(res?.data?.data?.trustScore) || 4.8;
      if (count > 0) {
        dispatch(updateTrustPilotReviewCount(count));
        dispatch(updateTrustPilotScore(score));
      }
    });

    const universitiesTask = within(fetchTopUniversities(market)).then(async (unis) => {
      if (!unis?.length) return;
      dispatch(setHomeV3Universities(unis));
      const first = unis[0];
      const rows = await within(
        fetchStaysNear(first.canonicalName, { lang, currency }, source.token),
        () => source.cancel("ssr budget"),
      );
      if (rows?.length)
        dispatch(setHomeV3Stays({ key: first.canonicalName, rows: toStayRows(rows) }));
    });

    try {
      await Promise.all([reviewsTask, trustpilotTask, universitiesTask]);
    } catch (err) {
      logger.log(`HomeV3 fetchHomeV3Data: ${err}`);
    }
    return null;
  };

export default fetchHomeV3Data;
